import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/PaymentIntentsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the payment-intents list.
//
// ⚠️⚠️ MONEY PATH. This module creates and confirms PaymentIntents — the object
// that MOVES money. The scope of this change is the LIST/TABLE layer only.
// Nothing here alters confirm/capture/cancel semantics, an idempotency key, or
// how `clientSecret` is handled. `add` and `update` in the service keep their
// existing call shape; the one change to `update` is the payload ALLOWLIST
// (see PaymentIntentsService.ts), which narrows what is sent rather than
// changing what any Stripe call does.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['paymentIntents', {customerId}, currentToken]` — a bare string plus a
// page token, which makes the cache page-shaped rather than list-shaped: every
// page click wrote a new cache entry and nothing could invalidate the list as a
// whole.
export const paymentIntentsKeys = {
    all: ['payment-intents'] as const,
    lists: () => [...paymentIntentsKeys.all, 'list'] as const,
    list: (customerId: string, filters: Record<string, string> = {}) =>
        [...paymentIntentsKeys.lists(), customerId, {filters}] as const,
    details: () => [...paymentIntentsKeys.all, 'detail'] as const,
    detail: (id: string) => [...paymentIntentsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which could
 * only hold ONE page at a time — going forward threw the previous page away, so
 * client-side search and sort could never see more than 10 rows.
 *
 * ⚠️ ONLY `customerId` REACHES THE SERVER. `PaymentIntentsFilterInput` declares
 * exactly one field:
 *
 *     input PaymentIntentsFilterInput { customerId: String }
 *
 * (verified against
 * BILLING/APIS/flexinet-stripe-api/src/appsync-api/src/schema/features/paymentIntents.graphql).
 *
 * There is NO `status` filter and no search argument. Sending one anyway would
 * be silently ignored — AppSync drops undeclared input fields without an error,
 * so a status filter would LOOK like it worked while returning the unfiltered
 * list. Status is therefore filtered client-side over the loaded window, and
 * the drawer says so. See usePaymentIntentsFilter.ts.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 */
export const usePaymentIntentsInfiniteQuery = (
    customerId?: string,
    filters: Record<string, string> = {},
) => {
    // `customerId` is OPTIONAL on the filter input, unlike payment-methods where
    // it is required. The index page lists every payment intent; the customer
    // EditPage tab passes an id to scope it. An empty filter object is a valid
    // request, so this query is NEVER disabled.
    const serverFilter: Record<string, string> = customerId
        ? {customerId}
        : {};

    return useInfiniteQuery({
        // Filters are in the key even though they are applied client-side: the
        // key must describe the data the caller is looking at, and keeping the
        // shape identical to the other modules means a later server-side filter
        // needs no key change.
        queryKey: paymentIntentsKeys.list(customerId ?? '', filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(serverFilter, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
    });
};

/**
 * Batch delete (cancel). The whole module key is invalidated on success so the
 * table reflects the removal without a manual reload (§8).
 *
 * ⚠️ MONEY PATH: the underlying `deleteRecord` service call is UNCHANGED. This
 * wraps the existing behaviour in structured keys and a notification, nothing
 * more. The confirmation modal in front of it (DeleteRecordsConfirmationButton)
 * is also unchanged.
 *
 * ⚠️ The success message is COUNT-AWARE. The previous implementation always
 * said "Payment intent deleted" (singular) even for a batch of twelve, which
 * reads as though eleven silently failed.
 */
export const useDeletePaymentIntentsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Payment intent deleted'
                    : `${ids.length} payment intents deleted`,
            );
            queryClient.invalidateQueries({queryKey: paymentIntentsKeys.all});
        },
        onError: notifyError,
    });
};
